// @flow

import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'

import {
  PENDING_STATUS,
  SEND_STATUS_TIMEOUT_MS,
  UNKNOWN_STATUS,
  getSendStatus,
  subscribeSendStatus,
} from '../../core/sendStatus'

// The SDK creates an event ID even without a client, so the ID alone does not mean
// the event is being sent (e.g. in dev, where Sentry is not initialised)
const isSendingEnabled = (): boolean => {
  try {
    return Boolean(Sentry.getCurrentHub().getClient())
  } catch (e) {
    return false
  }
}

// eslint-disable-next-line complexity
export const useSendStatus = (eventId: string): string => {
  const [enabled] = useState(() => Boolean(eventId) && isSendingEnabled())
  const [status, setStatus] = useState(() => (enabled ? getSendStatus(eventId) : ''))
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    if (!enabled) return undefined

    const unsubscribe = subscribeSendStatus(eventId, setStatus)
    const timer = setTimeout(() => setTimedOut(true), SEND_STATUS_TIMEOUT_MS)

    // The response may have arrived between the first render and the subscription
    setStatus(getSendStatus(eventId))

    return () => {
      unsubscribe()
      clearTimeout(timer)
    }
  }, [enabled, eventId])

  if (!enabled) return ''

  // A received code always wins: the timeout only replaces the pending state
  return status || (timedOut ? UNKNOWN_STATUS : PENDING_STATUS)
}
